'use client';
import React, { HTMLAttributes, useEffect, useRef } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';

function CartItem({
    className,
    title,
    subtitle,
    buttonLabel,
    price,
    children,
    currency,
    isDisabled,
    isProcessing
}: {
    title: string;
    subtitle?: string;
    buttonLabel?: string;
    price?: number;
    children: React.ReactNode;
    currency: string;
    isDisabled: boolean;
    isProcessing?: boolean;
} & Pick<HTMLAttributes<HTMLDivElement>, 'className'>): React.JSX.Element {
    const formatter = useFormat();
    const detailsRef = useRef<HTMLDetailsElement>(null);
    useEffect(() => {
        if (detailsRef.current) {
            detailsRef.current?.removeAttribute('open');
        }
    }, [isDisabled]);

    return (
        <details
            ref={detailsRef}
            className={cn('w-full group duration-150', className, {
                'cursor-not-allowed': isDisabled,
                'cursor-pointer': !isDisabled,
                'opacity-80': isProcessing
            })}>
            <summary
                className="flex flex-col w-full space-y-[6px]"
                onClick={(e) => {
                    if (isDisabled || isProcessing) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}>
                <div className="w-full flex flex-col">
                    <div className="flex space-x-[16px]">
                        <span className="grow text-body-bold-16 select-none">{title}</span>
                        {isProcessing ? (
                            <IconLoading16 className="animate-spin" />
                        ) : (
                            <span className="text-body-bold-16 shrink-0 select-none">
                                {formatter.number.formatCurrency((price ?? 0) / 100, currency)}
                            </span>
                        )}
                    </div>
                    {subtitle && <span className="text-body-14 select-none">{subtitle}</span>}
                </div>
                {buttonLabel && !isDisabled && (
                    <span
                        className={cn(
                            'text-[var(--primary-color)] text-overline-12 tracking-[1.6px]',
                            isDisabled || isProcessing ? 'opacity-50' : 'clickable-element'
                        )}>
                        {buttonLabel}
                    </span>
                )}
            </summary>
            <div
                onClick={() => {
                    detailsRef.current?.removeAttribute('open');
                }}>
                {children}
            </div>
        </details>
    );
}

export default CartItem;
